import { ColumnDef } from '@tanstack/react-table';
import { DataTable, DataTableSearchConfig } from '@/components/DataTable';
import { Checkbox } from '@/components/ui/checkbox';
import { useCallback, useMemo, useState } from 'react';
import { convertISODateToMMDDYYYY } from '@/lib/utils';
import {
  exportRecapZip,
  getRecapEmails,
  getRecaps,
  useRecapFilterOptions,
  useShareRecapMutation,
} from '@/services/recapService';
import { useRecapStore } from '@/stores/useRecapStore';
import type { RecapListItem, RecapListQueryParams, RecapListResponse } from '@/types/recap.types';
import { UseQueryOptions } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { Button } from '@/components/ui/button';
import { ChevronDown, Download, Loader2, Send } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import AlertBox from '@/components/AlertBox';
import { hasFeature } from '@/services/featureService';

type RecapFilter = 'ALL' | 'NIGHTLY' | 'WEEKLY';

const filterOptions: { label: string; value: RecapFilter }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'Nightly Recaps', value: 'NIGHTLY' },
  { label: 'Weekly Recaps', value: 'WEEKLY' },
];

const Recaps = () => {
  const [selectedRecaps, setSelectedRecaps] = useState<string[]>([]);
  const [tableData, setTableData] = useState<{ data: RecapListItem[]; rest?: unknown }>();
  const selectAll = !!tableData?.data?.length && tableData.data.every((row) => selectedRecaps.includes(row.recapId));
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareRecapIds, setShareRecapIds] = useState<string[]>([]);
  const [shareEmails, setShareEmails] = useState<string[]>([]);
  const [getRecapEmailIdIsLoading, setRecapEmailIdIsLoading] = useState(false);
  const exportingRecapIds = useRecapStore((state) => state.exportingRecapIds);
  const hasShareFeature = hasFeature('recap.share');
  const hasExportFeature = hasFeature('recap.export');
  const shareRecapMutation = useShareRecapMutation();

  const handleCheckboxChange = (recapId: string, isChecked: string | boolean) => {
    if (isChecked) {
      setSelectedRecaps((prev) => (prev.includes(recapId) ? prev : [...prev, recapId]));
    } else {
      setSelectedRecaps((prev) => prev.filter((id) => id !== recapId));
    }
  };

  const handleSelectAllChange = (isChecked: boolean) => {
    const currentPageIds = tableData?.data?.map((d) => d.recapId) ?? [];
    if (isChecked) {
      setSelectedRecaps((prev) => Array.from(new Set([...prev, ...currentPageIds])));
    } else {
      setSelectedRecaps((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    }
  };

  const apiGetWrapper = useCallback((params?: unknown, options?: { query?: UseQueryOptions }) => {
    const safeParams = (params || {}) as Record<string, unknown>;
    const { pageNo, ...restParams } = safeParams;
    const apiParams: RecapListQueryParams = {
      ...(restParams as unknown as RecapListQueryParams),
      page: pageNo as number,
      pageSize: safeParams.pageSize as number,
      frequency: (safeParams.frequency as RecapFilter) || 'ALL',
      sortField: safeParams.sortField as string | undefined,
      sortDirection: safeParams.sortDirection as 'asc' | 'desc' | undefined,
    };

    return getRecaps(apiParams, options as { query?: Partial<UseQueryOptions<RecapListResponse>> });
  }, []);

  const filterOptionsData = useRecapFilterOptions();

  const { clientOptions, recapNumbers } = useMemo(() => {
    const clients = filterOptionsData.data?.clients ?? [];
    const clientOptions = clients.map((c) => ({
      ...c,
      label1: c.label1 ?? c.label2 ?? c.value ?? '',
    }));
    const recapNumbers = filterOptionsData.data?.recapNumbers ?? [];
    return {
      clientOptions,
      recapNumbers,
    };
  }, [filterOptionsData.data]);

  const searchConfig: DataTableSearchConfig[] = useMemo(
    () => [
      {
        name: 'code',
        label: 'Search Clients',
        placeholder: 'Search Clients',
        fieldType: 'MULTISELECT',
        multiSelectOptions: clientOptions,
      },
      {
        name: 'fromDate',
        label: 'From Date',
        placeholder: 'Select Date',
        fieldType: 'DATE',
        disabledAt: (date: Date) => {
          const today = new Date();
          return date > today;
        },
      },
      {
        name: 'toDate',
        label: 'To Date',
        placeholder: 'Select Date',
        fieldType: 'DATE',
        disabledAt: (date: Date) => {
          const today = new Date();
          return date > today;
        },
        dependsOn: {
          field: 'fromDate',
          resetOnChange: true,
          disableBefore: true,
        },
      },
      {
        name: 'recapNumber',
        label: 'Search Recap',
        placeholder: 'Enter Recap Number',
        fieldType: 'MULTISELECT',
        multiSelectOptions: recapNumbers,
      },
    ],
    [clientOptions, recapNumbers]
  );

  const viewFilters = {
    buttonLabel: 'Client Preference',
    groups: [
      {
        label: 'Client Preference',
        name: 'frequency',
        options: filterOptions.map((f) => ({ label: f.label, value: f.value })),
      },
    ],
  };

  const handleBulkExport = useCallback(() => {
    if (selectedRecaps.length > 0) {
      void exportRecapZip(selectedRecaps);
    } else {
      toast({ description: 'Please select at least one recap to export' });
    }
  }, [selectedRecaps]);

  const handleBulkShare = useCallback(async () => {
    if (selectedRecaps.length === 0) {
      toast({ description: 'Please select at least one recap to share' });
      return;
    }

    try {
      setRecapEmailIdIsLoading(true);
      const emails = await getRecapEmails(selectedRecaps);
      const allEmails = [...emails.to, ...emails.cc].filter(Boolean);
      if (allEmails.length === 0) {
        toast({
          description: 'No recipient email addresses configured for this client',
          variant: 'destructive',
        });
        return;
      }
      setShareEmails(allEmails);
      setShareRecapIds(selectedRecaps);
      setShareDialogOpen(true);
    } catch {
      toast({
        description: 'Unable to fetch recipient emails',
        variant: 'destructive',
      });
    } finally {
      setRecapEmailIdIsLoading(false);
    }
  }, [selectedRecaps]);

  const handleConfirmShare = useCallback(() => {
    shareRecapMutation.mutate(
      { recapIds: shareRecapIds },
      {
        onSuccess: (data) => {
          const sent = data.results.filter((r) => r.status === 'SENT').length;
          const failed = data.results.filter((r) => r.status === 'FAILED').length;
          toast({
            description: `${sent} recap(s) shared successfully${failed > 0 ? `, ${failed} failed` : ''}`,
          });
          setSelectedRecaps([]);
          setShareDialogOpen(false);
        },
        onError: (error) => {
          displayError(error.response?.data);
          setShareDialogOpen(false);
        },
      }
    );
  }, [shareRecapIds, shareRecapMutation]);

  const handleRowExport = useCallback((recapId: string) => {
    void exportRecapZip([recapId]);
  }, []);

  const handleRowShare = useCallback(async (recapIds: string[]) => {
    try {
      setRecapEmailIdIsLoading(true);
      const emails = await getRecapEmails(recapIds);
      const allEmails = [...emails.to, ...emails.cc].filter(Boolean);
      if (allEmails.length === 0) {
        toast({
          description: 'No recipient email addresses configured for this client',
          variant: 'destructive',
        });
        return;
      }
      setShareEmails(allEmails);
      setShareRecapIds(recapIds);
      setShareDialogOpen(true);
    } catch {
      toast({
        description: 'Unable to fetch recipient emails',
        variant: 'destructive',
      });
    } finally {
      setRecapEmailIdIsLoading(false);
    }
  }, []);

  const bulkActions = useMemo(() => {
    const actions: { label: string; value: string; icon: React.ReactElement; onClick: () => void }[] = [];
    if (hasExportFeature) {
      actions.push({
        label: 'Export Recaps',
        value: 'exportRecaps',
        icon: <Download className="w-4 h-4" color="#334155" strokeWidth="1px" />,
        onClick: handleBulkExport,
      });
    }
    if (hasShareFeature) {
      actions.push({
        label: 'Share Recaps',
        value: 'shareRecaps',
        icon: <Send className="w-4 h-4" color="#334155" strokeWidth="1px" />,
        onClick: handleBulkShare,
      });
    }
    return actions;
  }, [hasExportFeature, hasShareFeature, handleBulkExport, handleBulkShare]);

  const columns: ColumnDef<RecapListItem>[] = useMemo(() => {
    return [
      {
        accessorKey: 'select',
        header: () => (
          <Checkbox
            id="select-all-recaps"
            checked={selectAll}
            onCheckedChange={(checked) => handleSelectAllChange(checked as boolean)}
          />
        ),
        size: 50,
        cell: ({ row }) => {
          const isChecked = selectedRecaps.includes(row.original.recapId);
          return (
            <div className="flex w-full min-w-[40px] mr-2">
              <Checkbox
                id={`recap-${row.original.recapId}`}
                checked={isChecked}
                onCheckedChange={(checked) => handleCheckboxChange(row.original.recapId, checked)}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'clientCode',
        header: 'Client #',
        enableSorting: true,
        cell: ({ cell }) => (
          <div className="flex w-full min-w-[80px] mr-2">
            <span>{cell.getValue() as string}</span>
          </div>
        ),
      },
      {
        accessorKey: 'clientName',
        header: 'Client Name',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex flex-col w-full min-w-[140px] mr-2">
            <span>{row.original.clientName ?? '-'}</span>
            {row.original.lastEmailSentDate && (
              <span className="text-[10px] text-[#969696]">
                Last email sent on {convertISODateToMMDDYYYY(row.original.lastEmailSentDate)}
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'recapNumber',
        header: 'Recap #',
        enableSorting: true,
        cell: ({ cell }) => (
          <div className="flex w-full min-w-[100px] mr-2">
            <span>{cell.getValue() as string}</span>
          </div>
        ),
      },
      {
        accessorKey: 'startDate',
        header: 'From Date',
        enableSorting: true,
        cell: ({ cell }) => (
          <div className="flex w-full min-w-[110px] mr-2">
            <span>{convertISODateToMMDDYYYY(cell.getValue() as string)}</span>
          </div>
        ),
      },
      {
        accessorKey: 'endDate',
        header: 'To Date',
        enableSorting: true,
        cell: ({ cell }) => (
          <div className="flex w-full min-w-[110px] mr-2">
            <span>{convertISODateToMMDDYYYY(cell.getValue() as string)}</span>
          </div>
        ),
      },
      ...(hasExportFeature
        ? [
            {
              accessorKey: 'export',
              header: '',
              cell: ({ row }: { row: { original: RecapListItem } }) => {
                const recap = row.original;
                const isExporting = exportingRecapIds.includes(recap.recapId);
                return (
                  <div className="flex flex-row gap-3 items-center min-w-[60px] justify-start">
                    {isExporting ? (
                      <Loader2
                        className="h-4 w-4 animate-spin text-[#0A2240]"
                        data-testid={`export-recap-loading-${recap.recapId}`}
                      />
                    ) : (
                      <Download
                        className="h-4 w-4 cursor-pointer text-[#0A2240] hover:text-[#1551B2]"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleRowExport(recap.recapId);
                        }}
                        data-testid={`export-recap-${recap.recapId}`}
                      />
                    )}
                  </div>
                );
              },
            },
          ]
        : []),
      ...(hasShareFeature
        ? [
            {
              accessorKey: 'share',
              header: '',
              cell: ({ row }: { row: { original: RecapListItem } }) => {
                const recap = row.original;
                return (
                  <div className="flex flex-row gap-3 items-center min-w-[60px] justify-start">
                    {getRecapEmailIdIsLoading ? (
                      <div className="h-4 w-4">
                        <Loader2 className="animate-spin" />
                      </div>
                    ) : (
                      <Send
                        className="h-4 w-4 cursor-pointer text-[#0A2240] hover:text-[#1551B2]"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await handleRowShare([recap.recapId]);
                        }}
                        data-testid={`share-recap-${recap.recapId}`}
                      />
                    )}
                  </div>
                );
              },
            },
          ]
        : []),
    ];
  }, [
    selectedRecaps,
    selectAll,
    tableData,
    hasExportFeature,
    hasShareFeature,
    handleRowExport,
    handleRowShare,
    exportingRecapIds,
    getRecapEmailIdIsLoading,
  ]);

  const summarySection = useMemo(() => {
    if (bulkActions.length === 0) return undefined;
    return (
      <div className="w-full flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger
            asChild
            className="focus:border-[#1551B2] !border-[1px] !border-primary min-w-[136px] w-fit h-[36px] !text-primary font-[500] text-sm"
          >
            <Button variant="outline" className="flex flex-row gap-2 align-middle">
              <span>Bulk Actions</span>
              <ChevronDown className="w-4 h-4" strokeWidth="1px" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56 mr-8">
            {bulkActions.map((action) => (
              <DropdownMenuItem key={action.value} onSelect={action.onClick}>
                <div className="!flex !w-full !flex-row justify-between">
                  <span className="text-primary text-sm font-[500]">{action.label}</span>
                  <span>{action.icon}</span>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }, [bulkActions]);

  return (
    <div className="flex w-full flex-col gap-6">
      <DataTable
        toolbar={{
          toolBarTitle: 'Recaps',
          filters: viewFilters,
        }}
        columns={columns}
        apiGet={apiGetWrapper}
        setRows={(data, rest) => setTableData({ data: data as RecapListItem[], rest })}
        search={searchConfig}
        summarySection={summarySection}
      />

      <AlertBox
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        title="Are you sure?"
        description={
          <span>
            Are you sure you want to share the recap with <strong>{shareEmails.join(', ')}</strong>?
          </span>
        }
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Send"
        confirmBtnClick={handleConfirmShare}
        isLoading={shareRecapMutation.isPending}
      />
    </div>
  );
};

export default Recaps;
